import { forwardRef } from "react";
import { cn } from "../../utils/cn";
import { CountBadge } from "./CountBadge";

export interface HeaderActionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: React.ReactNode;
  badgeCount?: number;
  badgeVariant?: "danger" | "primary" | "success";
}

/**
 * Standardized header action button with support for count badges.
 * Used for Notifications, Chat, and User Profile buttons in the main header.
 */
export const HeaderActionButton = forwardRef<
  HTMLButtonElement,
  HeaderActionButtonProps
>(
  (
    {
      icon,
      badgeCount = 0,
      badgeVariant = "danger",
      className,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        className={cn(
          "relative flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 lg:w-11 lg:h-11",
          "bg-slate-50 border border-slate-200 rounded-lg sm:rounded-xl",
          "hover:border-primary hover:bg-white hover:-translate-y-0.5 transition-all duration-300",
          "focus:outline-none ring-offset-2 focus:ring-2 ring-primary/20",
          "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0",
          className,
        )}
        {...props}
      >
        {icon}
        {badgeCount > 0 && (
          <CountBadge
            count={badgeCount}
            variant={badgeVariant}
            className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1"
          />
        )}
        {children}
      </button>
    );
  },
);

HeaderActionButton.displayName = "HeaderActionButton";
